import { HttpClient, HttpParams } from '@angular/common/http';
import { forkJoin, mergeMap, Observable, tap } from 'rxjs';
import { map } from 'rxjs/operators';
import { VirtualClinicApi } from '../store/virtual-clinic.store';
import {
  CacheService,
  ResourceConfigControlledService,
  ResourceConfigItem,
} from '@bindoc-projects/resource-lib/data';
import {
  AysFullyInpatientCase,
  AysVirtualClinic,
  AysVirtualClinicAdditionalInfo,
  AysVirtualClinicAddress,
  AysVirtualClinicAnnualAccount,
  AysVirtualClinicCaseNumber,
  AysVirtualClinicP21Import,
  AysVirtualClinicP21ImportResponse,
  AysVirtualClinicSpecialtyDepartment,
  AysVirtualClinicStaffing,
  VirtualClinicRelationGraph,
} from '@bindoc-projects/virtual-clinic-domain-lib';
import {
  BdResourceService,
  BdSharePermission,
} from '@bindoc-projects/resource-domain-lib';
import { BdAppEnvironmentService } from '@bindoc-projects/platform-lib/data';
import { BdPageable, BdPageableHelper, Paginator } from '@bindoc-projects/platform-lib/data';
import {
  BdAndCondition,
  BdCondition,
} from '@bindoc-projects/condition-domain-lib';
import {
  VirtualClinicAnalysisOperatingNumberNodeItem,
  VirtualClinicAnalysisOperatingNumberSingleRequestPart,
  VirtualClinicAnalysisPatientDistribution,
} from '@bindoc-projects/virtual-clinic-analysis-domain-lib';
import { BdAdministrativeDistrictId } from '@bindoc-projects/geographic-domain-lib';
import { AysVirtualClinicAnalysisService } from './virtual-clinic-analysis.service';
import {
  BdAdministrativeDistrictService,
  BdPostalCodeService,
} from '@bindoc-projects/geographic-lib/data';

export class VirtualClinicService
  extends ResourceConfigControlledService
  implements BdResourceService<AysVirtualClinic>
{
  protected _api: VirtualClinicApi;

  constructor(
    resourceConfig: ResourceConfigItem,
    environmentService: BdAppEnvironmentService,
    cache: CacheService,
    protected _httpClient: HttpClient,
    private readonly _virtualClinicAnalysisService: AysVirtualClinicAnalysisService,
    private readonly _postalCodeService: BdPostalCodeService,
    private readonly _districtService: BdAdministrativeDistrictService,
  ) {
    super(resourceConfig, environmentService, cache);
    this._api = new VirtualClinicApi(this.getBaseUrl());
  }

  public findAll(
    minPermission?: BdSharePermission,
    companyId?: string,
  ): Observable<AysVirtualClinic[]> {
    const url = this._api.virtualClinics();
    let params = new HttpParams();

    if (minPermission) {
      params = params.append('minPermission', minPermission);
    }

    if (companyId) {
      params = params.set('companyId', companyId);
    }

    return this._httpClient
      .get<unknown[]>(url, { params })
      .pipe(map((it: unknown[]) => AysVirtualClinic.fromObjArr(it)));
  }

  public findById(virtualClinicId: string): Observable<AysVirtualClinic> {
    const url = this._api.virtualClinicById(virtualClinicId);

    return this._cache.cacheObservable(
      this.key(virtualClinicId),
      this._httpClient.get(url).pipe(
        map(AysVirtualClinic.fromObj),
        map((it) => it.clone()),
      ),
    );
  }

  public create(virtualClinic: AysVirtualClinic): Observable<AysVirtualClinic> {
    const url = this._api.virtualClinics();

    return this._httpClient
      .post<AysVirtualClinic>(url, virtualClinic)
      .pipe(map(AysVirtualClinic.fromObj));
  }

  public update(clinic: AysVirtualClinic): Observable<void> {
    this.deleteCacheForKey(clinic.id);
    const url = this._api.virtualClinicById(clinic.id);
    return this._httpClient
      .post<AysVirtualClinic>(url, clinic)
      .pipe(map(() => null));
  }

  public getSpecialtyDepartments(
    virtualClinicId: string,
    sdKey?: string,
  ): Observable<AysVirtualClinicSpecialtyDepartment[]> {
    let url = this._api.specialtyDepartments(virtualClinicId);

    if (sdKey) {
      url = this._api.specialtyDepartment(virtualClinicId, sdKey);
    }

    return this._httpClient
      .get<unknown[]>(url)
      .pipe(map(AysVirtualClinicSpecialtyDepartment.fromObjArr));
  }

  public khtgLeistungsgruppen(): Observable<
    { key: string; displayName: { [locale: string]: string } }[]
  > {
    const url = this._api.leistungsgruppenKHTG();
    return this._cache.cacheObservable(
      url,
      this._httpClient.get<
        { key: string; displayName: { [locale: string]: string } }[]
      >(url),
    );
  }

  public fileOfTruthCategories(): Observable<string[]> {
    const url = `${this._api.fileOfTruth()}/categories`;
    return this._cache.cacheObservable(
      'fileOfTruthCategories',
      this._httpClient.get<string[]>(url),
    );
  }

  public fileOfTruthTypeOfEntries(): Observable<string[]> {
    const url = `${this._api.fileOfTruth()}/type-of-entries`;
    return this._cache.cacheObservable(
      'fileOfTruthTypeOfEntries',
      this._httpClient.get<string[]>(url),
    );
  }

  public fileOfTruthProcedureSubjects(): Observable<{
    [category: string]: string[];
  }> {
    const url = `${this._api.fileOfTruth()}/categories/procedure-subjects`;
    return this._cache.cacheObservable(
      'fileOfTruthProcedureSubjects',
      this._httpClient.get<{
        [category: string]: string[];
      }>(url),
    );
  }

  public fileOfTruthProcedureNames(): Observable<{
    [category: string]: string[];
  }> {
    const url = `${this._api.fileOfTruth()}/categories/procedure-names`;
    return this._cache.cacheObservable(
      'fileOfTruthProcedureNames',
      this._httpClient.get<{
        [category: string]: string[];
      }>(url),
    );
  }

  public saveAddress(
    virtualClinicId: string,
    address: AysVirtualClinicAddress,
  ): Observable<AysVirtualClinicAddress> {
    const url = this._api.address(virtualClinicId);
    const body = {
      ...address,
      administrativeDistrictId:
        address?.administrativeDistrictId?.getNumericValue(),
    };

    return this._httpClient.post<AysVirtualClinicAddress>(url, body).pipe(
      tap(() => this.deleteCacheForKey(virtualClinicId)),
      map((json) => AysVirtualClinicAddress.fromObject(json)),
    );
  }

  public getCaseNumber(
    virtualClinicId: string,
    specialtyDepartmentId?: string,
    year?: number,
  ): Promise<AysVirtualClinicCaseNumber[]> {
    let url = this._api.caseNumbers(virtualClinicId);

    if (specialtyDepartmentId) {
      url = `${this._api.specialtyDepartment(
        virtualClinicId,
        specialtyDepartmentId,
      )}case-number`;
    }

    let params = new HttpParams();
    if (year) {
      params = params.append('year', String(year));
    }

    return this._httpClient
      .get<unknown[]>(url, { params: params })
      .pipe(map(AysVirtualClinicCaseNumber.fromObjArr))
      .toPromise();
  }

  public saveCaseNumber(
    caseNumber: AysVirtualClinicCaseNumber,
  ): Promise<AysVirtualClinicCaseNumber> {
    const url = this._api.caseNumbers(caseNumber.virtualClinicId);

    return this._httpClient
      .post(url, caseNumber)
      .pipe(map(AysVirtualClinicCaseNumber.fromObj))
      .toPromise();
  }

  public deleteCaseNumber(
    virtualClinicId: string,
    year?: number,
  ): Promise<unknown> {
    const url = this._api.caseNumbers(virtualClinicId);

    let params = new HttpParams();
    if (year) {
      params = params.append('year', String(year));
    }

    return this._httpClient.delete(url, { params: params }).toPromise();
  }

  public getStaffing(
    virtualClinicId: string,
    specialtyDepartmentId?: string,
    year?: number,
  ): Promise<AysVirtualClinicStaffing[]> {
    let url = this._api.staffing(virtualClinicId);

    if (specialtyDepartmentId) {
      url = `${this._api.specialtyDepartment(
        virtualClinicId,
        specialtyDepartmentId,
      )}staffing`;
    }

    let params = new HttpParams();
    if (year) {
      params = params.append('year', String(year));
    }

    return this._httpClient
      .get<unknown[]>(url, { params: params })
      .pipe(map(AysVirtualClinicStaffing.fromObjArr))
      .toPromise();
  }

  public saveStaffing(
    staffing: AysVirtualClinicStaffing,
  ): Promise<AysVirtualClinicStaffing> {
    const url = this._api.staffing(staffing.virtualClinicId);

    return this._httpClient
      .post(url, staffing)
      .pipe(map(AysVirtualClinicStaffing.fromObj))
      .toPromise();
  }

  public deleteStaffing(
    virtualClinicId: string,
    year?: number,
  ): Promise<unknown> {
    const url = this._api.staffing(virtualClinicId);

    let params = new HttpParams();
    if (year) {
      params = params.append('year', String(year));
    }

    return this._httpClient.delete(url, { params: params }).toPromise();
  }

  public saveSpecialtyDepartment(
    specialtyDepartment: AysVirtualClinicSpecialtyDepartment,
  ): Promise<AysVirtualClinicSpecialtyDepartment> {
    const url = this._api.specialtyDepartments(
      specialtyDepartment.virtualClinicId,
    );

    return this._httpClient
      .post(url, specialtyDepartment)
      .pipe(map((value) => AysVirtualClinicSpecialtyDepartment.fromObj(value)))
      .toPromise();
  }

  public deleteSpecialtyDepartment(
    virtualClinicId: string,
    sdKey: string,
  ): Promise<unknown> {
    const url = this._api.specialtyDepartment(virtualClinicId, sdKey);

    return this._httpClient.delete(url).toPromise();
  }

  public getFullyInpatientCases(
    virtualClinicId: string,
    page: Paginator,
    condition: BdCondition = new BdAndCondition(),
  ): Observable<BdPageable<AysFullyInpatientCase>> {
    const url = this._api.fullyInpatientCases(virtualClinicId);

    const defaultBody: {
      condition: { [id: string]: unknown };
    } = {
      condition: condition.toJSON(),
    };

    return this._httpClient
      .post<BdPageable<AysFullyInpatientCase>>(url, defaultBody, {
        params: page.appendTo(new HttpParams()),
      })
      .pipe(
        map((value) =>
          BdPageableHelper.responseToPage<
            AysFullyInpatientCase,
            AysFullyInpatientCase
          >(value, (item) => AysFullyInpatientCase.fromObj(item)),
        ),
      );
  }

  public getFullyInpatientCase(
    virtualClinicId: string,
    caseId: string,
  ): Observable<AysFullyInpatientCase> {
    const url = this._api.fullyInpatientCase(virtualClinicId, caseId);

    return this._httpClient
      .get<unknown>(url)
      .pipe(map((it) => AysFullyInpatientCase.fromObj(it)));
  }

  public getAnnualAccounts(
    virtualClinicId: string,
    year?: number,
  ): Promise<AysVirtualClinicAnnualAccount[]> {
    const url = this._api.annualAccount(virtualClinicId);
    let params = new HttpParams();
    if (year) {
      params = params.append('year', String(year));
    }

    return this._httpClient
      .get<unknown[]>(url, { params: params })
      .pipe(map(AysVirtualClinicAnnualAccount.fromObjArr))
      .toPromise();
  }

  public saveAnnualAccount(
    annualAccount: AysVirtualClinicAnnualAccount,
  ): Promise<unknown> {
    const url = this._api.annualAccount(annualAccount.virtualClinicId);

    return this._httpClient.post(url, annualAccount).toPromise();
  }

  public deleteAnnualAccounts(
    virtualClinicId: string,
    year?: number,
  ): Promise<unknown> {
    const url = this._api.annualAccount(virtualClinicId);
    let params = new HttpParams();
    if (year) {
      params = params.append('year', String(year));
    }

    return this._httpClient.delete(url, { params: params }).toPromise();
  }

  public getAdditionalInfo(
    virtualClinicId: string,
    year?: number,
  ): Promise<AysVirtualClinicAdditionalInfo[]> {
    const url = this._api.additionalInfo(virtualClinicId);
    let params = new HttpParams();
    if (year) {
      params = params.append('year', String(year));
    }

    return this._httpClient
      .get<unknown[]>(url, { params: params })
      .pipe(map(AysVirtualClinicAdditionalInfo.fromObjArr))
      .toPromise();
  }

  public saveAdditionalInfo(
    additionalInfo: AysVirtualClinicAdditionalInfo,
  ): Promise<unknown> {
    const url = this._api.additionalInfo(additionalInfo.virtualClinicId);

    return this._httpClient.post(url, additionalInfo).toPromise();
  }

  public deleteAdditionalInfo(
    virtualClinicId: string,
    year?: number,
  ): Promise<unknown> {
    const url = this._api.additionalInfo(virtualClinicId);

    let params = new HttpParams();
    if (year) {
      params = params.append('year', String(year));
    }

    return this._httpClient.delete(url, { params: params }).toPromise();
  }

  public daVinciSystemIds(virtualClinicId: string): Observable<string[]> {
    const url = this._api.daVinciSystemIds(virtualClinicId);
    return this._cache.cacheObservable(
      'daVinciSystemIds-' + virtualClinicId,
      this._httpClient.get<string[]>(url),
    );
  }

  public opSaals(virtualClinicId: string): Observable<string[]> {
    const url = this._api.opSaals(virtualClinicId);
    return this._cache.cacheObservable(
      'opSaals-' + virtualClinicId,
      this._httpClient.get<string[]>(url),
    );
  }

  public getRelatedVirtualClinics(
    virtualClinicId: string,
  ): Observable<VirtualClinicRelationGraph> {
    const url = this._api.relatedVirtualClinics(virtualClinicId);

    return this._httpClient.get<VirtualClinicRelationGraph>(url);
  }

  public getKoStImports(
    virtualClinicId: string,
  ): Observable<AysVirtualClinicP21Import[]> {
    const url = this._api.kostImports(virtualClinicId);

    return this._httpClient
      .get<AysVirtualClinicP21ImportResponse[]>(url)
      .pipe(
        map((imports) =>
          imports.map((it) => AysVirtualClinicP21Import.fromObj(it)),
        ),
      );
  }

  public patientsByAdministrativeDistricts(
    virtualClinicId: string,
    condition: BdCondition,
  ): Observable<VirtualClinicAnalysisPatientDistribution> {
    const operatingNumberReq: VirtualClinicAnalysisOperatingNumberSingleRequestPart =
      {
        type: 'VirtualClinicOperatingNumberSingle',
        virtualClinicId: virtualClinicId,
        groupBy: [
          {
            fieldName: 'administrativeDistrict',
            aggregation: { name: 'SUM' },
            sort: { sortBy: 'LABEL', direction: 'DESC' },
          },
          {
            fieldName: 'postalCode',
            aggregation: { name: 'SUM' },
            sort: { sortBy: 'LABEL', direction: 'DESC' },
          },
        ],
        term: {
          type: 'fullyInpatientCaseOperatingNumber',
          operatingNumberId: 'counts',
          caseFilter: condition.toJSON(),
        },
      };

    return this._virtualClinicAnalysisService
      .resolve({ REQ: operatingNumberReq })
      .pipe(
        map((response) => {
          if (response.requests['REQ'].statusType === 'error') {
            throw new Error(response.requests['REQ'].errors[0].text['de']);
          } else if (response.requests['REQ'].statusType === 'warning') {
            throw new Error(response.requests['REQ'].warning[0].text['de']);
          }
          {
            return response;
          }
        }),
        mergeMap((response) => {
          const administrativeDistrictIds = [];
          const postalCodes = [];

          response.requests['REQ'].groupedValues.forEach(
            (administrativeDistrictItem) => {
              administrativeDistrictIds.push(
                administrativeDistrictItem.label.key,
              );

              (
                administrativeDistrictItem as VirtualClinicAnalysisOperatingNumberNodeItem
              ).groupedValues.forEach((postalCodeItem) => {
                postalCodes.push(postalCodeItem.label.key);
              });
            },
          );

          return forkJoin([
            this._postalCodeService.findAll(postalCodes, 'FIRST'),
            this._districtService.findAll(
              administrativeDistrictIds.map((districtId) =>
                BdAdministrativeDistrictId.createFromString(districtId),
              ),
            ),
          ]).pipe(
            map((it) => ({
              virtualClinicAnalysisResponse: response,
              postalCodesMapping: it[0],
              administrativeDistrictMapping: it[1],
            })),
          );
        }),
        map((it) => {
          return VirtualClinicAnalysisPatientDistribution.of(
            it.virtualClinicAnalysisResponse.requests['REQ'],
            it.administrativeDistrictMapping,
            it.postalCodesMapping,
          );
        }),
      );
  }
}
